"use client";

import clsx from "clsx";
import {useTranslation} from "react-i18next";
import Icon, {ICON_SIZES} from "@/components/library/Icon";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip";
import {useGailGptReadAloud} from "@/hooks/useGailGptReadAloud";
import {
   EMPTY_CAPTIONS_TRACK_SRC,
   getMediaTrackSrcLang,
} from "@/utils/common/mediaTrackLanguage";

interface GailGptReadAloudProps {
   text: string;
   conversationId?: string;
   messageId?: string;
   messageIndex?: number;
}

const formatTime = (seconds: number): string => {
   const safeSeconds = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
   const minutes = Math.floor(safeSeconds / 60);
   const remainingSeconds = Math.floor(safeSeconds % 60);
   return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

const PILL_BUTTON_CLASSES = clsx(
   "inline-flex items-center justify-center size-7 rounded-md",
   "text-700 opacity-80 hover:opacity-100",
   "hover:bg-black/10 dark:hover:bg-white/10",
   "transition-all duration-150",
   "disabled:cursor-not-allowed disabled:opacity-30"
);
export default function GailGptReadAloud({
   text,
   conversationId,
   messageId,
   messageIndex,
}: GailGptReadAloudProps) {
   const {t, i18n} = useTranslation("ask");
   const captionSrcLang = getMediaTrackSrcLang(i18n);
   const {
      audioRef,
      canGenerateSpeech,
      collapsePlayer,
      currentAbsoluteTime,
      error,
      generatedDuration,
      handleProgressClick,
      handleProgressKeyDown,
      isExpanded,
      isGenerating,
      isPlaying,
      jumpBy,
      progressPercentage,
      progressRef,
      togglePlayPause,
   } = useGailGptReadAloud({text, conversationId, messageId, messageIndex});

   const playPauseLabel = isPlaying ? t("readAloud.pauseResponse") : t("readAloud.playResponse");
   const generatingLabel = t("readAloud.generating");

   if (!isExpanded) {
      return (
         <>
            <audio ref={audioRef} preload="none" className="hidden">
               <track
                  kind="captions"
                  src={EMPTY_CAPTIONS_TRACK_SRC}
                  srcLang={captionSrcLang}
                  label={t("a11y.captionsUnavailable")}
               />
            </audio>
            <Tooltip>
               <TooltipTrigger asChild>
                  <button
                     type="button"
                     onClick={togglePlayPause}
                     disabled={!canGenerateSpeech}
                     className={clsx(
                        "inline-flex items-center justify-center size-8 rounded-lg",
                        "opacity-70 hover:opacity-100",
                        "hover:bg-black/10 dark:hover:bg-white/10",
                        "transition-all duration-150",
                        !canGenerateSpeech && "cursor-not-allowed opacity-40"
                     )}
                     aria-label={t("readAloud.playResponse")}
                  >
                     <Icon icon="volume-high" size={ICON_SIZES.SM} />
                  </button>
               </TooltipTrigger>
               <TooltipContent side="bottom">{t("readAloud.tooltip")}</TooltipContent>
            </Tooltip>
         </>
      );
   }

   return (
      <>
         <audio ref={audioRef} preload="none" className="hidden">
            <track
               kind="captions"
               src={EMPTY_CAPTIONS_TRACK_SRC}
               srcLang={captionSrcLang}
               label={t("a11y.captionsUnavailable")}
            />
         </audio>
         <fieldset
            className={clsx(
               "m-0 inline-flex h-8 items-center gap-0.5 rounded-full border-0 pl-1 pr-1.5",
               "bg-black/[0.04] dark:bg-white/10"
            )}
         >
            <legend className="sr-only">{t("readAloud.tooltip")}</legend>
            <Tooltip>
               <TooltipTrigger asChild>
                  <button
                     type="button"
                     onClick={() => jumpBy(-5)}
                     className={PILL_BUTTON_CLASSES}
                     aria-label={t("readAloud.rewind")}
                  >
                     <Icon icon="rotate-left" size={ICON_SIZES.SM} />
                  </button>
               </TooltipTrigger>
               <TooltipContent side="bottom">{t("readAloud.rewind")}</TooltipContent>
            </Tooltip>

            <Tooltip>
               <TooltipTrigger asChild>
                  <button
                     type="button"
                     onClick={togglePlayPause}
                     className={clsx(PILL_BUTTON_CLASSES, "text-800 opacity-100")}
                     aria-label={playPauseLabel}
                  >
                     <Icon icon={isPlaying ? "pause" : "play"} size={ICON_SIZES.SM} />
                  </button>
               </TooltipTrigger>
               <TooltipContent side="bottom">{playPauseLabel}</TooltipContent>
            </Tooltip>

            <Tooltip>
               <TooltipTrigger asChild>
                  <button
                     type="button"
                     onClick={() => jumpBy(5)}
                     className={PILL_BUTTON_CLASSES}
                     aria-label={t("readAloud.forward")}
                  >
                     <Icon icon="rotate-right" size={ICON_SIZES.SM} />
                  </button>
               </TooltipTrigger>
               <TooltipContent side="bottom">{t("readAloud.forward")}</TooltipContent>
            </Tooltip>

            <button
               type="button"
               ref={progressRef}
               className={clsx(
                  "group/progress relative mx-1.5 h-1 w-32 cursor-pointer rounded-full bg-black/15 dark:bg-white/20",
                  isGenerating && "animate-pulse"
               )}
               onClick={handleProgressClick}
               onKeyDown={handleProgressKeyDown}
               aria-label={t("readAloud.progress")}
               role="slider"
               aria-valuemin={0}
               aria-valuemax={Math.round(generatedDuration || currentAbsoluteTime)}
               aria-valuenow={Math.round(currentAbsoluteTime)}
            >
               <div
                  className="absolute inset-y-0 left-0 rounded-full bg-lula transition-[width] duration-150"
                  style={{width: `${progressPercentage}%`}}
               />
               <div
                  className={clsx(
                     "absolute top-1/2 size-2.5 -translate-y-1/2 rounded-full bg-lula shadow-sm",
                     "opacity-0 transition-opacity duration-150",
                     "group-hover/progress:opacity-100 group-focus-visible/progress:opacity-100"
                  )}
                  style={{left: `calc(${progressPercentage}% - 5px)`}}
               />
            </button>

            <span className="px-1 text-[11px] font-medium tabular-nums text-600">
               {formatTime(currentAbsoluteTime)}
               <span className="mx-0.5 opacity-60">/</span>
               {isGenerating ? "-:--" : formatTime(generatedDuration)}
            </span>

            {isGenerating && (
               <Tooltip>
                  <TooltipTrigger asChild>
                     <output
                        className="inline-flex size-6 items-center justify-center"
                        aria-live="polite"
                        aria-label={generatingLabel}
                     >
                        <span
                           className="size-3 animate-spin rounded-full border-[1.5px] border-current border-t-transparent text-700 opacity-60"
                           aria-hidden="true"
                        />
                     </output>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">{generatingLabel}</TooltipContent>
               </Tooltip>
            )}

            <Tooltip>
               <TooltipTrigger asChild>
                  <button
                     type="button"
                     onClick={collapsePlayer}
                     className={clsx(PILL_BUTTON_CLASSES, "size-6")}
                     aria-label={t("readAloud.close")}
                  >
                     <Icon icon="xmark" size={ICON_SIZES.SM} />
                  </button>
               </TooltipTrigger>
               <TooltipContent side="bottom">{t("readAloud.close")}</TooltipContent>
            </Tooltip>
         </fieldset>

         {error && (
            <output className="self-center text-xs text-500" aria-live="polite">
               {error}
            </output>
         )}
      </>
   );
}
